import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Users, ShieldCheck, Sparkles, Mail, Compass, TrendingUp,
  Loader2, RefreshCw, MousePointerClick, Percent, Eye,
} from "lucide-react";
import { api } from "../lib/api";
import { useSeo } from "../lib/useSeo";
import { Button } from "../components/ui/button";

const TABS = [
  { key: "overview", label: "Overzicht", icon: TrendingUp },
  { key: "lifecycle", label: "Lifecycle e-mails", icon: Mail },
  { key: "scam", label: "Scam Check Funnel", icon: ShieldCheck },
  { key: "premium", label: "Premium Funnel", icon: Sparkles },
  { key: "acquisition", label: "Acquisitie & verkeer", icon: Compass },
  { key: "conversions", label: "Conversies", icon: Percent },
];

const RANGES = [
  { key: 1, label: "Vandaag" },
  { key: 7, label: "7 dagen" },
  { key: 30, label: "30 dagen" },
  { key: 90, label: "90 dagen" },
];

const fmt = (v) => (v === null || v === undefined ? "—" : typeof v === "number" ? v.toLocaleString("nl-NL") : v);
const pct = (v) => (v === null || v === undefined ? "—" : `${v}%`);

const Kpi = ({ icon: Icon, label, value, sub, testid, tint = "bg-brandblue-light text-brandblue" }) => (
  <div className="rounded-3xl border border-border bg-white p-5 shadow-sm" data-testid={testid}>
    <div className={`flex h-10 w-10 items-center justify-center rounded-2xl ${tint}`}>
      <Icon size={18} />
    </div>
    <div className="mt-3 font-display text-3xl font-extrabold text-navy">{value}</div>
    <div className="mt-1 text-sm font-medium text-slate-500">{label}</div>
    {sub != null && <div className="mt-0.5 text-xs text-slate-400">{sub}</div>}
  </div>
);

// -- Overview tab ------------------------------------------------------------

const OverviewTab = ({ days }) => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    setData(null); setErr("");
    api(`/admin/marketing/overview?days=${days}`).then(setData).catch((e) => setErr(e.message));
  }, [days]);

  if (err) return <div className="rounded-xl bg-danger/10 p-4 text-sm text-danger">{err}</div>;
  if (!data) return <div className="flex items-center gap-2 text-slate-500"><Loader2 className="animate-spin" size={16} /> Laden…</div>;
  const k = data.kpis;

  const maxV = Math.max(1, ...data.daily.map((d) => Math.max(d.visitors, d.new_accounts * 10, d.new_premium * 20)));

  return (
    <div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi icon={Eye} label="Unieke bezoekers" value={fmt(k.unique_visitors)} sub={`${fmt(k.total_pageviews)} pageviews`} testid="kpi-visitors" />
        <Kpi icon={Users} label="Nieuwe accounts" value={fmt(k.new_accounts)} sub={k.new_accounts_trend_pct != null ? `${k.new_accounts_trend_pct > 0 ? "+" : ""}${k.new_accounts_trend_pct}% vs vorige periode` : "geen trend-data"} testid="kpi-new-accounts" />
        <Kpi icon={Sparkles} label="Nieuwe Premium (window)" value={fmt(k.new_premium)} sub={`Free→Premium ${pct(k.signup_to_premium_pct)}`} testid="kpi-new-premium" tint="bg-amber-100 text-amber-600" />
        <Kpi icon={Sparkles} label="Actieve Premium totaal" value={fmt(k.active_premium_total)} testid="kpi-active-premium" tint="bg-amber-100 text-amber-600" />
        <Kpi icon={Mail} label="Lifecycle mails verstuurd" value={fmt(k.lifecycle_sent)} testid="kpi-lc-sent" />
        <Kpi icon={MousePointerClick} label="Lifecycle clicks" value={fmt(k.lifecycle_clicks)} sub={`CTR ${pct(k.lifecycle_ctr_pct)}`} testid="kpi-lc-clicks" />
        <Kpi icon={ShieldCheck} label="Scam nudge views" value={fmt(k.scam_nudge_views)} testid="kpi-nudge-views" />
        <Kpi icon={MousePointerClick} label="Scam nudge clicks" value={fmt(k.scam_nudge_clicks)} sub={`CTR ${pct(k.scam_nudge_ctr_pct)}`} testid="kpi-nudge-clicks" />
      </div>

      <div className="mt-8 rounded-3xl border border-border bg-white p-6 shadow-sm">
        <h3 className="font-display text-lg font-extrabold text-navy">Dagelijkse trend</h3>
        <p className="text-xs text-slate-500">Bezoekers · nieuwe accounts · nieuwe premium — laatste {days} dagen</p>
        <div className="mt-5 flex items-end gap-1 overflow-x-auto pb-1" data-testid="overview-chart">
          {data.daily.length === 0 && <div className="text-sm text-slate-400">Geen data in deze periode.</div>}
          {data.daily.map((d) => (
            <div key={d.date} className="flex min-w-[28px] flex-col items-center gap-0.5" title={`${d.date} — ${d.visitors} bezoekers, ${d.new_accounts} accounts, ${d.new_premium} premium`}>
              <div className="flex h-[120px] w-[24px] flex-col justify-end gap-[2px]">
                <div className="w-full rounded-t-sm bg-amber-400" style={{ height: `${(d.new_premium / maxV) * 120}px` }} />
                <div className="w-full bg-brandblue" style={{ height: `${(d.new_accounts / maxV) * 120}px` }} />
                <div className="w-full bg-brandblue-light" style={{ height: `${(d.visitors / maxV) * 120}px` }} />
              </div>
              <span className="rotate-45 text-[9px] text-slate-400">{d.date.slice(5)}</span>
            </div>
          ))}
        </div>
        <div className="mt-3 flex gap-4 text-xs text-slate-500">
          <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-brandblue-light" /> Bezoekers</span>
          <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-brandblue" /> Accounts</span>
          <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-amber-400" /> Premium</span>
        </div>
      </div>
    </div>
  );
};

// -- Lifecycle tab -----------------------------------------------------------

const LifecycleTab = ({ days }) => {
  const [data, setData] = useState(null);
  const [clicks, setClicks] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    setData(null); setClicks(null); setErr("");
    api(`/admin/marketing/lifecycle?days=${days}`).then(setData).catch((e) => setErr(e.message));
    api(`/admin/marketing/lifecycle-clicks?days=${days}&limit=100`).then(setClicks).catch(() => {});
  }, [days]);

  if (err) return <div className="rounded-xl bg-danger/10 p-4 text-sm text-danger">{err}</div>;
  if (!data) return <div className="flex items-center gap-2 text-slate-500"><Loader2 className="animate-spin" size={16} /> Laden…</div>;

  const stageLabel = { day0: "Dag 0 — Welkom", day3: "Dag 3 — Scam Check", day7: "Dag 7 — Match Score", day14: "Dag 14 — Premium tools" };

  return (
    <div>
      <div className="mb-4 text-sm text-slate-500">Eligible users (marketing consent, niet uitgeschreven): <strong className="text-navy">{fmt(data.eligible_users_total)}</strong></div>
      <div className="grid gap-4 md:grid-cols-2">
        {data.stages.map((s) => (
          <div key={s.stage} className="rounded-3xl border border-border bg-white p-5 shadow-sm" data-testid={`lc-stage-${s.stage}`}>
            <div className="flex items-center justify-between">
              <h4 className="font-display font-extrabold text-navy">{stageLabel[s.stage] || s.stage}</h4>
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-slate-500">{s.stage}</span>
            </div>
            <div className="mt-4 grid grid-cols-3 gap-2 text-sm">
              <div><div className="text-slate-400 text-xs">Verstuurd</div><div className="font-bold text-navy">{fmt(s.sent)}</div></div>
              <div><div className="text-slate-400 text-xs">Clicks</div><div className="font-bold text-navy">{fmt(s.clicks)}</div></div>
              <div><div className="text-slate-400 text-xs">CTR</div><div className="font-bold text-navy">{pct(s.ctr_pct)}</div></div>
              <div><div className="text-slate-400 text-xs">→ /premium</div><div className="font-bold text-navy">{fmt(s.premium_page_visits)}</div></div>
              <div><div className="text-slate-400 text-xs">→ /prijzen</div><div className="font-bold text-navy">{fmt(s.pricing_page_visits)}</div></div>
              <div><div className="text-slate-400 text-xs">Premium</div><div className="font-bold text-amber-600">{fmt(s.conversions)}</div></div>
            </div>
            <div className="mt-3 text-xs text-slate-500">Conv. rate: <strong className="text-navy">{pct(s.conversion_pct)}</strong></div>
          </div>
        ))}
      </div>

      <div className="mt-8 rounded-3xl border border-border bg-white p-6 shadow-sm">
        <h3 className="font-display text-lg font-extrabold text-navy">Vergelijking</h3>
        <table className="mt-4 w-full text-sm" data-testid="lc-comparison-table">
          <thead>
            <tr className="text-left text-slate-400">
              <th className="pb-2">Stage</th><th>Verstuurd</th><th>Clicks</th><th>CTR</th><th>/premium</th><th>/prijzen</th><th>Conversies</th><th>Conv %</th>
            </tr>
          </thead>
          <tbody>
            {data.stages.map((s) => (
              <tr key={s.stage} className="border-t border-border">
                <td className="py-2 font-semibold">{s.stage}</td>
                <td>{fmt(s.sent)}</td><td>{fmt(s.clicks)}</td><td>{pct(s.ctr_pct)}</td>
                <td>{fmt(s.premium_page_visits)}</td><td>{fmt(s.pricing_page_visits)}</td>
                <td className="font-bold text-amber-600">{fmt(s.conversions)}</td><td>{pct(s.conversion_pct)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="mt-3 text-xs italic text-slate-400">{data.note}</p>
      </div>

      {clicks?.clicks?.length > 0 && (
        <div className="mt-8 rounded-3xl border border-border bg-white p-6 shadow-sm">
          <h3 className="font-display text-lg font-extrabold text-navy">Recente lifecycle clicks</h3>
          <table className="mt-4 w-full text-sm" data-testid="lc-click-log">
            <thead>
              <tr className="text-left text-slate-400">
                <th className="pb-2">Datum</th><th>Stage</th><th>User</th><th>Destination</th><th>Premium later?</th>
              </tr>
            </thead>
            <tbody>
              {clicks.clicks.slice(0, 50).map((c, i) => (
                <tr key={i} className="border-t border-border">
                  <td className="py-1.5 text-xs">{c.ts?.slice(0, 16).replace("T", " ")}</td>
                  <td className="text-xs font-semibold">{c.stage}</td>
                  <td className="font-mono text-[11px]">
                    {c.user_id ? <Link to={`/admin?user=${c.user_id}`} className="text-brandblue underline">{c.user_id.slice(0, 8)}…</Link> : "—"}
                  </td>
                  <td className="text-xs">{c.destination}</td>
                  <td>{c.premium_later == null ? "—" : c.premium_later ? <span className="text-success">✓</span> : <span className="text-slate-400">✗</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// -- Scam funnel -------------------------------------------------------------

const ScamTab = ({ days }) => {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    setD(null); setErr("");
    api(`/admin/marketing/scam-funnel?days=${days}`).then(setD).catch((e) => setErr(e.message));
  }, [days]);
  if (err) return <div className="rounded-xl bg-danger/10 p-4 text-sm text-danger">{err}</div>;
  if (!d) return <div className="flex items-center gap-2 text-slate-500"><Loader2 className="animate-spin" size={16} /> Laden…</div>;

  const funnel = [
    { label: "Scam Checks uitgevoerd", value: d.scam_checks_performed },
    { label: "Nudge getoond", value: d.nudge_views },
    { label: "Nudge geklikt", value: d.nudge_clicks },
    { label: "Naar /premium", value: d.clicks_to_premium },
    { label: "Naar /prijzen", value: d.clicks_to_prijzen },
    { label: "Premium geworden", value: d.premium_conversions },
  ];
  const max = Math.max(1, ...funnel.map((f) => f.value));

  return (
    <div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi icon={ShieldCheck} label="Scam Checks" value={fmt(d.scam_checks_performed)} testid="scam-kpi-checks" />
        <Kpi icon={Eye} label="Nudge views" value={fmt(d.nudge_views)} sub={`${fmt(d.unique_viewers)} unieke sessies`} testid="scam-kpi-views" />
        <Kpi icon={MousePointerClick} label="Clicks" value={fmt(d.nudge_clicks)} sub={`CTR ${pct(d.ctr_pct)}`} testid="scam-kpi-clicks" />
        <Kpi icon={Sparkles} label="Premium conversies" value={fmt(d.premium_conversions)} sub={pct(d.conversion_pct)} tint="bg-amber-100 text-amber-600" testid="scam-kpi-conv" />
      </div>

      <div className="mt-8 rounded-3xl border border-border bg-white p-6 shadow-sm">
        <h3 className="font-display text-lg font-extrabold text-navy">Funnel</h3>
        <div className="mt-4 space-y-2">
          {funnel.map((f, i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="w-40 text-sm text-slate-600">{f.label}</div>
              <div className="relative h-8 flex-1 rounded-full bg-slate-100">
                <div className="absolute left-0 top-0 h-full rounded-full bg-brandblue" style={{ width: `${(f.value / max) * 100}%` }} />
              </div>
              <div className="w-16 text-right font-bold text-navy">{fmt(f.value)}</div>
            </div>
          ))}
        </div>
        <p className="mt-4 text-xs italic text-slate-400">{d.note}</p>
      </div>

      {d.risk_breakdown?.length > 0 && (
        <div className="mt-8 rounded-3xl border border-border bg-white p-6 shadow-sm">
          <h3 className="font-display text-lg font-extrabold text-navy">Risk-level breakdown</h3>
          <div className="mt-4 flex flex-wrap gap-2">
            {d.risk_breakdown.map((r) => (
              <span key={r.risk} className="rounded-full bg-slate-100 px-3 py-1 text-sm">
                <strong className="text-navy">{r.risk}</strong>: {fmt(r.count)}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// -- Premium funnel ----------------------------------------------------------

const PremiumTab = ({ days }) => {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    setD(null); setErr("");
    api(`/admin/marketing/premium-funnel?days=${days}`).then(setD).catch((e) => setErr(e.message));
  }, [days]);
  if (err) return <div className="rounded-xl bg-danger/10 p-4 text-sm text-danger">{err}</div>;
  if (!d) return <div className="flex items-center gap-2 text-slate-500"><Loader2 className="animate-spin" size={16} /> Laden…</div>;

  const max = Math.max(1, ...d.steps.map((s) => s.count));

  return (
    <div>
      <div className="rounded-3xl border border-border bg-white p-6 shadow-sm">
        <h3 className="font-display text-lg font-extrabold text-navy">Premium acquisitie-funnel</h3>
        <div className="mt-4 space-y-2" data-testid="premium-funnel-steps">
          {d.steps.map((s) => (
            <div key={s.step} className="flex items-center gap-3">
              <div className="w-56 text-sm font-medium text-slate-600">{s.step}</div>
              <div className="relative h-8 flex-1 rounded-full bg-slate-100">
                <div className="absolute left-0 top-0 h-full rounded-full bg-navy" style={{ width: `${(s.count / max) * 100}%` }} />
              </div>
              <div className="w-24 text-right font-bold text-navy">{fmt(s.count)}</div>
              <div className="w-20 text-right text-xs text-slate-500">{s.pct_from_prev != null ? pct(s.pct_from_prev) : "—"}</div>
            </div>
          ))}
        </div>
        <p className="mt-4 text-xs italic text-slate-400">{d.note}</p>
      </div>

      {d.tool_clicks?.length > 0 && (
        <div className="mt-8 rounded-3xl border border-border bg-white p-6 shadow-sm">
          <h3 className="font-display text-lg font-extrabold text-navy">Populairste Premium-tools</h3>
          <table className="mt-4 w-full text-sm" data-testid="premium-tools-table">
            <thead><tr className="text-left text-slate-400"><th className="pb-2">Tool</th><th>Clicks</th></tr></thead>
            <tbody>
              {d.tool_clicks.map((t) => (
                <tr key={t.tool} className="border-t border-border"><td className="py-2 font-semibold">{t.tool}</td><td>{fmt(t.count)}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// -- Acquisition -------------------------------------------------------------

const AcqTab = ({ days }) => {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    setD(null); setErr("");
    api(`/admin/marketing/acquisition?days=${days}`).then(setD).catch((e) => setErr(e.message));
  }, [days]);
  if (err) return <div className="rounded-xl bg-danger/10 p-4 text-sm text-danger">{err}</div>;
  if (!d) return <div className="flex items-center gap-2 text-slate-500"><Loader2 className="animate-spin" size={16} /> Laden…</div>;

  return (
    <div className="rounded-3xl border border-border bg-white p-6 shadow-sm">
      <h3 className="font-display text-lg font-extrabold text-navy">Traffic sources</h3>
      <table className="mt-4 w-full text-sm" data-testid="acq-table">
        <thead>
          <tr className="text-left text-slate-400">
            <th className="pb-2">Bron</th><th>Bezoekers</th><th>Accounts</th><th>Premium</th><th>Acct %</th><th>Prem %</th>
          </tr>
        </thead>
        <tbody>
          {d.sources.map((s) => (
            <tr key={s.source} className="border-t border-border">
              <td className="py-2 font-semibold">{s.source}</td>
              <td>{fmt(s.visitors)}</td>
              <td>{fmt(s.accounts)}</td>
              <td className="font-bold text-amber-600">{fmt(s.premium)}</td>
              <td>{pct(s.account_conv_pct)}</td>
              <td>{pct(s.premium_conv_pct)}</td>
            </tr>
          ))}
          {d.sources.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-slate-400">Geen verkeersdata in deze periode.</td></tr>}
        </tbody>
      </table>
      <p className="mt-3 text-xs italic text-slate-400">{d.note}</p>
    </div>
  );
};

// -- Conversions -------------------------------------------------------------

const ConvTab = ({ days }) => {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    setD(null); setErr("");
    api(`/admin/marketing/conversions?days=${days}`).then(setD).catch((e) => setErr(e.message));
  }, [days]);
  if (err) return <div className="rounded-xl bg-danger/10 p-4 text-sm text-danger">{err}</div>;
  if (!d) return <div className="flex items-center gap-2 text-slate-500"><Loader2 className="animate-spin" size={16} /> Laden…</div>;

  return (
    <div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi icon={Sparkles} label="Actieve Premium" value={fmt(d.total_active_premium)} testid="conv-total-active" tint="bg-amber-100 text-amber-600" />
        <Kpi icon={TrendingUp} label="Premium — laatste 7d" value={fmt(d.premium_last_7d)} sub={d.premium_7d_trend_pct != null ? `${d.premium_7d_trend_pct > 0 ? "+" : ""}${d.premium_7d_trend_pct}% vs vorige 7d` : "geen trend-data"} testid="conv-7d" />
        <Kpi icon={TrendingUp} label="Premium — laatste 30d" value={fmt(d.premium_last_30d)} sub={d.premium_30d_trend_pct != null ? `${d.premium_30d_trend_pct > 0 ? "+" : ""}${d.premium_30d_trend_pct}% vs vorige 30d` : "geen trend-data"} testid="conv-30d" />
      </div>

      <div className="mt-8 rounded-3xl border border-border bg-white p-6 shadow-sm">
        <h3 className="font-display text-lg font-extrabold text-navy">Attributie — alle premium-users</h3>
        <p className="text-xs text-slate-500">Deterministische toewijzing per user, prioriteit: lifecycle → scam nudge → premium hub → unattributed.</p>
        <table className="mt-4 w-full text-sm" data-testid="attr-table">
          <thead><tr className="text-left text-slate-400"><th className="pb-2">Bucket</th><th>Users</th></tr></thead>
          <tbody>
            {Object.entries(d.attribution).map(([k, v]) => (
              <tr key={k} className="border-t border-border">
                <td className="py-2 font-semibold">{k}</td>
                <td>{fmt(v)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// -- Root page ---------------------------------------------------------------

const AdminMarketing = () => {
  useSeo({ title: "Marketing & Growth · Admin", description: "Marketing & Growth analytics" });
  const [tab, setTab] = useState("overview");
  const [days, setDays] = useState(30);
  const [nonce, setNonce] = useState(0);

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Link to="/admin" className="inline-flex items-center gap-1 hover:text-navy" data-testid="admin-back"><ArrowLeft size={14} /> Admin</Link>
          </div>
          <h1 className="mt-1 font-display text-3xl font-extrabold text-navy" data-testid="admin-marketing-title">Marketing & Growth</h1>
        </div>
        <div className="flex items-center gap-2" data-testid="range-picker">
          {RANGES.map((r) => (
            <button
              key={r.key}
              type="button"
              data-testid={`range-${r.key}`}
              onClick={() => setDays(r.key)}
              className={`rounded-full px-3 py-1.5 text-sm font-semibold transition ${days === r.key ? "bg-navy text-amber-300" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
            >
              {r.label}
            </button>
          ))}
          <Button variant="ghost" size="sm" onClick={() => setNonce((n) => n + 1)} data-testid="refresh-btn">
            <RefreshCw size={14} className="mr-1" /> Ververs
          </Button>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-1 border-b border-border">
        {TABS.map((tb) => (
          <button
            key={tb.key}
            type="button"
            data-testid={`tab-${tb.key}`}
            onClick={() => setTab(tb.key)}
            className={`inline-flex items-center gap-1.5 border-b-2 px-4 py-2.5 text-sm font-semibold transition ${
              tab === tb.key ? "border-brandblue text-brandblue" : "border-transparent text-slate-500 hover:text-navy"
            }`}
          >
            <tb.icon size={14} /> {tb.label}
          </button>
        ))}
      </div>

      <div className="mt-6" key={`${tab}-${days}-${nonce}`}>
        {tab === "overview" && <OverviewTab days={days} />}
        {tab === "lifecycle" && <LifecycleTab days={days} />}
        {tab === "scam" && <ScamTab days={days} />}
        {tab === "premium" && <PremiumTab days={days} />}
        {tab === "acquisition" && <AcqTab days={days} />}
        {tab === "conversions" && <ConvTab days={days} />}
      </div>
    </div>
  );
};

export default AdminMarketing;
